import type { ChatSessionModel, ChatSessionParticipant } from "@/api/models"
import type { ShareFilter, SharingRelationship } from "@/features/sharing/utils/share-filter"

import { matchesShareFilter as matchesShareFilterBase } from "@/features/sharing/utils/share-filter"

/**
 * The sharing relationship between the current user and a chat session, as
 * surfaced in the sidebar "Chats" section.
 *
 * - `private` — you own it and it isn't shared with anyone else.
 * - `shared_with_me` — someone shared it with you (you're a reader).
 * - `shared_by_me` — you own it and shared it with at least one other person.
 *
 * Ownership transfer ("someone made you the owner") is tracked separately via
 * the `isOwnerTransfer` flag, because such a chat is now owned by you and
 * usually also shared back to the original owner — i.e. it is a special case of
 * `shared_by_me` that deserves its own marker.
 */
export type ChatSharingRelationship = SharingRelationship

export type ChatShareFilter = ShareFilter

export type ChatSharingState = {
  relationship: ChatSharingRelationship
  /** True when you are the current owner but were not the original creator. */
  isOwnerTransfer: boolean
  /** For `shared_with_me`: the participant who shared the chat with you. */
  sharer?: ChatSessionParticipant
  /** For `shared_by_me`: the other participants the chat is shared with (excludes you). */
  recipients: ChatSessionParticipant[]
}

/**
 * Derive the current user's sharing relationship to a chat session from the
 * data already present on `ChatSessionModel`.
 *
 * Pure and side-effect free so it can be unit-tested and memoised cheaply.
 */
export function getChatSharingState(
  session: ChatSessionModel,
  currentUserOid: string | undefined
): ChatSharingState {
  if (!session) {
    return { relationship: "private", isOwnerTransfer: false, recipients: [] }
  }

  const participants = session.participants ?? []

  // The reader role is the authoritative signal that a chat was shared with us.
  if (session.currentUserRole === "reader") {
    const sharer =
      participants.find((p) => p.role === "owner") ?? participants.find((p) => p.isOriginalOwner)
    return { relationship: "shared_with_me", isOwnerTransfer: false, sharer, recipients: [] }
  }

  // Without a known current-user OID we can't reliably tell our own participant
  // entry from the others (the recipients filter would keep everyone), so fall
  // back to a conservative private state rather than misclassify an owned chat
  // as shared. Readers are already handled above and don't depend on the OID.
  if (!currentUserOid) {
    return { relationship: "private", isOwnerTransfer: false, recipients: [] }
  }

  // Otherwise we own it (explicit owner role, or a legacy session with no role
  // whose userId matches us).
  const recipients = participants.filter((p) => p.oid !== currentUserOid)

  // Ownership was transferred to us when we're the current owner and a former
  // owner remains in the participant list. The backend flags the demoted former
  // owner with isFormerOwner on transfer (it survives in the list payload),
  // which is the only reliable signal here: after a transfer the session's
  // userId is reassigned to the new owner, so a userId comparison can't detect it.
  const isOwnerTransfer =
    session.currentUserRole === "owner" && participants.some((p) => p.isFormerOwner)

  const relationship: ChatSharingRelationship = recipients.length > 0 ? "shared_by_me" : "private"

  return { relationship, isOwnerTransfer, recipients }
}

/** Whether a chat with the given sharing state should appear under the active filter. */
export function matchesShareFilter(state: ChatSharingState, filter: ChatShareFilter): boolean {
  return matchesShareFilterBase(state.relationship, filter)
}
